import { DateTime, DateTimeUnit, Duration } from 'luxon';

/**
 * Converts a date to a Luxon DateTime object.
 * @param date - The date to convert.
 * @param zone - The zone to convert to (default: 'utc').
 * @returns The Luxon DateTime object.
 */
export const toLuxon = (date: DateTime | Date | string, zone = 'utc'): DateTime => {
    if (DateTime.isDateTime(date)) {
        return date.setZone(zone);
    }

    if (date instanceof Date) {
        return DateTime.fromJSDate(date, { zone });
    }

    return DateTime.fromISO(date, { zone });
};

/**
 * Checks if two dates are the same.
 * @param date1 - The first date.
 * @param date2 - The second date.
 * @param unit - The unit of comparison (default: 'day').
 * @returns True if the dates are the same, false otherwise.
 */
export const isSame = (
    date1: DateTime | Date | string,
    date2: DateTime | Date | string,
    unit: DateTimeUnit = 'day',
): boolean => {
    const luxonDate1 = toLuxon(date1);
    const luxonDate2 = toLuxon(date2);

    return luxonDate1.hasSame(luxonDate2, unit);
};

/**
 * Checks if the first date is before the second date.
 * @param date1 - The first date.
 * @param date2 - The second date.
 * @returns True if the first date is before the second date, false otherwise.
 */
export const isBefore = (
    date1: DateTime | Date | string,
    date2: DateTime | Date | string,
): boolean => {
    const luxonDate1 = toLuxon(date1);
    const luxonDate2 = toLuxon(date2);

    return luxonDate1 < luxonDate2;
};

/**
 * Checks if the first date is after the second date.
 * @param date1 - The first date.
 * @param date2 - The second date.
 * @returns True if the first date is after the second date, false otherwise.
 */
export const isAfter = (
    date1: DateTime | Date | string,
    date2: DateTime | Date | string,
): boolean => {
    const luxonDate1 = toLuxon(date1);
    const luxonDate2 = toLuxon(date2);

    return luxonDate1 > luxonDate2;
};

/**
 * Calculates the difference between two dates.
 * @param date1 - The first date chronologically.
 * @param date2 - The second date chronologically.
 * @param unit - The unit of difference (default: 'day').
 * @returns The duration between the two dates.
 */
export const getDifference = (
    date1: DateTime | Date | string,
    date2: DateTime | Date | string,
    unit: DateTimeUnit = 'day',
): Duration => {
    const luxonDate1 = toLuxon(date1);
    const luxonDate2 = toLuxon(date2);

    return luxonDate2.diff(luxonDate1, unit);
};

/**
 * Parses a string into a Luxon DateTime object.
 * @param dateTime - The string to parse (format: 'HH:mm').
 * @returns The parsed Luxon DateTime object, or null if the input is invalid.
 */
export const parseDateTime = (dateTime?: string): DateTime | null => {
    if (!dateTime || !/^\d{2}:\d{2}$/.test(dateTime)) {
        return null;
    }

    return DateTime.fromFormat(dateTime, 'HH:mm');
};

/**
 * Parses a string into a Luxon Duration object.
 * @param duration - The string to parse (format: 'HH:mm').
 * @returns The parsed Luxon Duration object, or null if the input is invalid.
 */
export const parseDuration = (duration?: string): Duration | null => {
    if (!duration || !/^\d{2}:\d{2}$/.test(duration)) {
        return null;
    }

    const [hours, minutes] = duration.split(':').map((part) => parseInt(part, 10));

    return Duration.fromObject({ hours, minutes });
};

/**
 * Converts a Date object to an ISO string, otherwise returns undefined.
 * @param date - The date to convert.
 * @param zone - The zone to convert to (default: 'utc').
 * @returns The ISO string or undefined.
 */
export const dateStringOrUndefined = (date?: Date | null, zone?: string): string | undefined => {
    if (!date) {
        return undefined;
    }

    return toLuxon(date, zone).toISO() || undefined;
};

/**
 * Converts a date string to Date object, otherwise returns null.
 * @param date - The date string to convert.
 * @param zone - The zone to convert to (default: 'utc').
 * @returns The Date object or null.
 */
export const dateOrNull = (date?: string | null, zone?: string): Date | null => {
    if (!date) {
        return null;
    }

    return toLuxon(date, zone).toJSDate();
};
